/**
 * Beside one value of the master config, when containers keep their own:
 * "2 own it" (hover: who, and their values) and a real button, "Override
 * 2", that gives them the master's — the master twin of a follower's
 * "Own value · Use binom's" (InheritTag). Nothing when every container
 * follows, and nothing in a container's editor.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { PencilLine, Undo2 } from 'lucide-react';
import type { DropGenSection } from '@trafficpulse/shared';

import { Tooltip } from '@/components/ui';

import { useMasterPush } from '../master-push-context';
import { OwnersCard } from './OwnersCard';

const BUTTON =
  'inline-flex h-7 items-center gap-1.5 rounded-lg border border-input bg-background px-2.5 text-[12px] font-medium text-foreground/80 shadow-sm transition-colors duration-200 hover:border-primary/50 hover:bg-primary/5 hover:text-primary disabled:pointer-events-none disabled:opacity-50';

/** "Override N" — the one way a value's own copies go back to the master's. */
export const OverrideButton: FC<{ section: DropGenSection; count: number }> = ({ section, count }) => {
  const push = useMasterPush();
  const blocked = push.blocked(section);
  return (
    <Tooltip content={blocked ?? `Drop their own value — they take ${push.masterName}’s`}>
      <span>
        <button type="button" disabled={Boolean(blocked)} onClick={() => push.ask(section)} className={BUTTON}>
          <Undo2 className="h-3 w-3" aria-hidden /> Override {count}
        </button>
      </span>
    </Tooltip>
  );
};

export const OwnersTag: FC<{ section: DropGenSection }> = ({ section }) => {
  const push = useMasterPush();
  const owners = push.owners(section);
  if (!push.active || owners.length === 0) return null;
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      <Tooltip
        content={<OwnersCard section={section} owners={owners} masterName={push.masterName} />}
        contentClassName="min-w-[220px] border border-border bg-popover px-3 py-2.5 text-popover-foreground"
      >
        <span className="inline-flex cursor-default items-center gap-1 text-[12px] font-medium text-warning">
          <PencilLine className="h-3 w-3" aria-hidden /> {owners.length} own it
        </span>
      </Tooltip>
      <OverrideButton section={section} count={owners.length} />
    </span>
  );
};
